import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Modal, Button, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faTimes } from '@fortawesome/free-solid-svg-icons';
import parse from 'html-react-parser';
import axios from 'axios';
import './TruApproval.css';

const TruApproval = ({ brokerData }) => {
    const [showVideo, setShowVideo] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [termsConditions, setTermsConditions] = useState(null);
    const [loading, setLoading] = useState(false);
    const [videoLoading, setVideoLoading] = useState(false);
    const videoRef = useRef(null);
    const thumbnailRef = useRef(null);

    // URL for the TRU approval application
    const truApprovalUrl = brokerData ? `https://www.swmc.com/ApplyNow/?lof=${brokerData.loID}` : '#';

    // Thumbnail path
    const thumbnailPath = `${process.env.REACT_APP_CDN || ''}brokers-landingpages-resources/images/truapproval.webp`;

    // YouTube video embedding options
    const youtubeEmbedUrl = "https://www.youtube.com/embed/7shKprV80dQ?autoplay=1&rel=0&enablejsapi=1";

    // Fetch terms and conditions data
    useEffect(() => {
        const fetchTermsConditions = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_CDN}common-components/jsons/truTermsConditions.json`);
                setTermsConditions(response.data);
            } catch (error) {
                console.error("Error fetching terms and conditions:", error);
            }
        };

        fetchTermsConditions();
    }, []);

    // Handle video play click
    const handlePlayVideo = () => {
        setVideoLoading(true);
        setShowVideo(true);
    };

    // Handle video close
    const handleCloseVideo = () => {
        setShowVideo(false);
        // Reset iframe src to stop video playback
        if (videoRef.current) {
            const iframe = videoRef.current;
            iframe.src = iframe.src;
        }
    };

    // Handle iframe load event
    const handleVideoLoad = () => {
        setVideoLoading(false);
    };

    // Handle modal open
    const handleOpenModal = () => {
        setLoading(true);
        setShowModal(true);
        setTimeout(() => setLoading(false), 500); // Simulate loading for better UX
    };

    return (
        <div id="truapproval" className="tru-approval-section">
            <Container>
                <Row className="align-items-center tru-approval-row">
                    {/* Left side - Video/Thumbnail */}
                    <Col lg={6} className="tru-video-container mb-4 mb-lg-0">
                        <div className="tru-thumbnail-wrapper" ref={thumbnailRef}>
                            {!showVideo ? (
                                <>
                                    <img
                                        src={thumbnailPath}
                                        alt="TRU Approval Thumbnail"
                                        className="tru-thumbnail"
                                        onClick={handlePlayVideo}
                                    />
                                    <div className="play-button-overlay" onClick={handlePlayVideo}>
                                        <div className="play-button-circle">
                                            <FontAwesomeIcon icon={faPlay} className="play-icon" />
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className="video-container">
                                    {videoLoading && (
                                        <div className="video-loading">
                                            <Spinner animation="border" variant="primary" />
                                        </div>
                                    )}
                                    <button className="video-close-btn" onClick={handleCloseVideo}>
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                    <iframe
                                        ref={videoRef}
                                        src={youtubeEmbedUrl}
                                        title="TRU Approval Video"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                        onLoad={handleVideoLoad}
                                    ></iframe>
                                </div>
                            )}
                        </div>
                        <div
                            className="tru-video-link tru-video-link-mobile"
                            onClick={handlePlayVideo}
                        >
                            <span className="play-icon-small">
                                <FontAwesomeIcon icon={faPlay} />
                            </span>
                            TRU Approval<sup>®</sup>: Your First Step to Homeownership video
                        </div>
                    </Col>

                    {/* Right side - Content */}
                    <Col lg={6} className="tru-content">
                        <h2 className="dark-blue-text bold">
                            <a
                                href={truApprovalUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Get TRU Approvals Today!"
                                className="applyNowUrl"
                            >Get a TRU Approval<sup>®</sup> Today!
                            </a>
                        </h2>

                        <p className="tru-paragraph">
                            We offer TRU Approvals exclusively through our lender partner.
                        </p>

                        <p className="tru-paragraph">
                            Know how much you can afford and stand out from other buyers with a loan approval equal in strength to a cash offer.
                        </p>

                        <p className="tru-paragraph">
                            A TRU Approved offer has NO LOAN CONTINGENCIES and has lender's Conditional Guarantee.<br />
                            <a
                                href="#"
                                className="terms-link"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleOpenModal();
                                }}
                            >
                                (click here for terms and conditions)
                            </a>
                        </p>

                        <div
                            className="tru-video-link tru-video-link-desktop"
                            onClick={handlePlayVideo}
                        >
                            <span className="play-icon-small">
                                <FontAwesomeIcon icon={faPlay} />
                            </span>
                            TRU Approval<sup>®</sup>: Your First Step to Homeownership video
                        </div>
                    </Col>
                </Row>
            </Container>

            {/* Terms and Conditions Modal */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                size="lg"
                centered
                dialogClassName="terms-modal"
            >
                <Modal.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : termsConditions ? (
                        <div className="terms-content">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h3 className="terms-title">{termsConditions.title}</h3>
                                <Button
                                    variant="link"
                                    className="close-button"
                                    onClick={() => setShowModal(false)}
                                >
                                    <FontAwesomeIcon icon={faTimes} />
                                </Button>
                            </div>

                            <ul className="terms-list">
                                {termsConditions.conditions.map((condition, index) => (
                                    <li key={index} className="condition-item">
                                        {condition.main}
                                        {condition.subItems.length > 0 && (
                                            <ul className="sub-items-list">
                                                {condition.subItems.map((item, subIndex) => (
                                                    <li key={subIndex}>{item}</li>
                                                ))}
                                            </ul>
                                        )}
                                    </li>
                                ))}
                            </ul>

                            <div className="limitation-section">
                                <h3>{termsConditions.limitation.title}</h3>
                                <p>{termsConditions.limitation.text}</p>
                            </div>

                            <div className="footnotes-section">
                                {termsConditions.footnotes.map((footnote, index) => (
                                    <p key={index} className="footnote">
                                        [{footnote.id}] {footnote.text} {" "}
                                        {footnote.link && (
                                            <a
                                                href={footnote.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="footnote-link"
                                            >
                                                {parse(footnote.link)}
                                            </a>
                                        )}
                                    </p>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            <p>Unable to load terms and conditions.</p>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default TruApproval;